import React, { useState, useEffect } from 'react'
import '../../assets/styles/referandearn.css';
import '../../assets/styles/popup.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import commonService from '../../services/otherServices';
import Popup from '../../components/Common/Error';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from "axios";

const ReferAndEarn = () => {
    const [showSubmitPopup, setShowSubmitPopup] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    interface FormData {
        name: string;
        email: string;
        job_title: string;
        company_name: string;
        phone_number: string;
    }

    interface FormErrors {
        name?: string;
        email?: string;
        job_title?: string;
        company_name?: string;
        phone_number?: string;
    }

    const [formData, setFormData] = useState<FormData>({
        name: '',
        email: '',
        job_title: '',
        company_name: '',
        phone_number: '',
    });

    const [errors, setErrors] = useState<FormErrors>({});
    const [touched, setTouched] = useState<Record<keyof FormData, boolean>>({
        name: false,
        email: false,
        job_title: false,
        company_name: false,
        phone_number: false,
    });

    const validateField = (name: keyof FormData, value: string): string => {
        // Only name & email are required
        if ((name === 'name' || name === 'email') && !value.trim()) {
            return "This field is required";
        }

        // Skip validation if optional field is empty
        if (!value.trim()) return "";

        switch (name) {
            case 'email':
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(value)) {
                    return "Please enter a valid email address";
                }
                break;

            case 'phone_number':
                const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
                if (!phoneRegex.test(value.replace(/[\s\-\(\)]/g, ''))) {
                    return "Please enter a valid phone number";
                }
                break;

            case 'name':
                if (value.trim().length < 2) {
                    return "Name must be at least 2 characters";
                }
                break;
        }

        return "";
    };


    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};
        let isValid = true;

        (['name', 'email'] as (keyof FormData)[]).forEach((fieldName) => {
            const error = validateField(fieldName, formData[fieldName]);
            if (error) {
                newErrors[fieldName] = error;
                isValid = false;
            }
        });

        setErrors(newErrors);
        return isValid;
    };


    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        const fieldName = name as keyof FormData;

        setFormData(prev => ({
            ...prev,
            [fieldName]: value
        }));

        // Validate field on change if it's been touched
        if (touched[fieldName]) {
            const error = validateField(fieldName, value);
            setErrors(prev => ({
                ...prev,
                [fieldName]: error
            }));
        }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name } = e.target;
        const fieldName = name as keyof FormData;

        // Mark field as touched
        setTouched(prev => ({
            ...prev,
            [fieldName]: true
        }));

        // Validate the field
        const error = validateField(fieldName, formData[fieldName]);
        setErrors(prev => ({
            ...prev,
            [fieldName]: error
        }));
    };

    const isFormComplete = () => {
        return (
            formData.name.trim() !== '' &&
            formData.email.trim() !== '' &&
            !errors.name &&
            !errors.email
        );
    };

    const onSubmit = async () => {
        // Mark all fields as touched when form is submitted
        const allTouched = Object.keys(formData).reduce((acc, key) => {
            acc[key as keyof FormData] = true;
            return acc;
        }, {} as Record<keyof FormData, boolean>);

        setTouched(allTouched);

        // Validate entire form
        if (!validateForm()) {
            toast.error("Please fix the errors in the form before submitting");
            return;
        }

        try {
            setIsLoading(true);
            const response = await commonService.referAndEarn(formData);
            // On success
            setShowSubmitPopup(true);
            setFormData({
                name: '',
                email: '',
                job_title: '',
                company_name: '',
                phone_number: '',
            });
            setTouched({
                name: false,
                email: false,
                job_title: false,
                company_name: false,
                phone_number: false,
            });
            setErrors({});
        } catch (err: any) {
                      
             setErrorMsg(
                 err.response?.message ||
                 "Failed to submit referral. Please try again."
             );
               
        } finally {

            setIsLoading(false);
        }
    };

    const getInputClassName = (fieldName: keyof FormData): string => {
        const baseClass = "dashboard-input";
        if (!touched[fieldName]) return baseClass;

        if (errors[fieldName]) {
            return `${baseClass} invalid-input`;
        }

        if (formData[fieldName].trim()) {
            return `${baseClass} valid-input`;
        }

        return baseClass;
    };

    return (
        <>
            <div className="container">
                <div>
                    <p className="share-grow">Share & Grow Together</p>
                    <p className="network">Help your network grow smarter with DataCaptive — and get rewarded!</p>
                </div>

                <div className="refer-and-earn-box mb-5">
                    <p className="colleagues">Invite your colleagues or business contacts to explore DataCaptive's verified B2B data solutions. Once your referral is verified and approved, you'll receive 500 credits in your account.</p>

                    <div className="row g-3">
                        {/* ROW 1 */}
                        <div className="col-md-6">
                            <div className="dashboard-input-group">
                                <label className="dashboard-input-label">Full Name *</label>
                                <input
                                    type="text"
                                    className={getInputClassName('name')}
                                    placeholder="Enter your full name"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    name="name"
                                    value={formData.name}
                                />
                                {touched.name && errors.name && (
                                    <div className="error-message">{errors.name}</div>
                                )}
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div className="dashboard-input-group">
                                <label className="dashboard-input-label">Business Email *</label>
                                <input
                                    type="email"
                                    className={getInputClassName('email')}
                                    placeholder="Enter your business email"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    name="email"
                                    value={formData.email}
                                />
                                {touched.email && errors.email && (
                                    <div className="error-message">{errors.email}</div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="row g-3 mt-2">
                        {/* ROW 2 */}
                        <div className="col-md-6">
                            <div className="dashboard-input-group">
                                <label className="dashboard-input-label">Company Name</label>
                                <input
                                    type="text"
                                    className={getInputClassName('company_name')}
                                    placeholder="Enter your company name"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    name="company_name"
                                    value={formData.company_name}
                                />
                                {/* {touched.company_name && errors.company_name && (
                                    <div className="error-message">{errors.company_name}</div>
                                )} */}
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div className="dashboard-input-group">
                                <label className="dashboard-input-label">Job Title</label>
                                <input
                                    type="text"
                                    className={getInputClassName('job_title')}
                                    placeholder="Enter your job title"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    name="job_title"
                                    value={formData.job_title}
                                />
                                {/* {touched.job_title && errors.job_title && (
                                    <div className="error-message">{errors.job_title}</div>
                                )} */}
                            </div>
                        </div>
                    </div>

                    <div className="row g-3 mt-2">
                        {/* ROW 3 */}
                        <div className="col-md-6">
                            <div className="dashboard-input-group">
                                <label className="dashboard-input-label">Phone Number</label>
                                <input
                                    type="tel"
                                    className={getInputClassName('phone_number')}
                                    placeholder="Enter your phone number"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    name="phone_number"
                                    value={formData.phone_number}
                                />
                                {/* {touched.phone_number && errors.phone_number && (
                                    <div className="error-message">{errors.phone_number}</div>
                                )} */}
                            </div>
                        </div>
                    </div>

                    {/* SUBMIT */}
                    <div className="d-flex justify-content-start mt-4">
                        <button
                            type="button"
                            className={`submit-request-btn ${isFormComplete() ? 'active' : ''}`}
                            disabled={!isFormComplete() || isLoading}
                            onClick={onSubmit}
                        >
                            {isLoading ? (
                                <>
                                    <span
                                        className="spinner-border spinner-border-sm me-2"
                                        role="status"
                                        aria-hidden="true"
                                    />
                                    Submitting...
                                </>
                            ) : "Submit Referral"}
                        </button>
                    </div>
                </div>
            </div>
            <Popup
                isOpen={!!errorMsg}
                onClose={() => setErrorMsg("")}
                title={errorMsg}
                iconSrc="/images/grey-cancel-cta.png"
            />
            {/* success popup */}

            {showSubmitPopup && (
                <div className="submit-popup-overlay">
                    <div className="submit-popup">
                        {/* Close icon */}
                        <div className="d-flex justify-content-end">
                            <img src="/images/close-active.png" alt="" className="submit-popup-close"
                                onClick={() => setShowSubmitPopup(false)}
                            />
                        </div>

                        <div className="d-flex justify-content-center">
                            <img src="/images/hand-shak.png" alt="" />
                        </div>

                        {/* Content */}
                        <p className="submit-popup-title">Referral Submitted Successfully!</p>
                        <p className="submit-popup-text mt-3" style={{ textAlign: "center" }}>
                            Thank you for referring DataCaptive.<br /> Our team will review your submission, and once verified,<br /> <strong>500 credits</strong> will be added to your account.
                        </p>
                    </div>
                </div>
            )}
            {/* <ToastContainer position="top-right" autoClose={5000} /> */}
        </>
    )
}

export default ReferAndEarn